import React from 'react';
import {arrayOf, bool, func, shape, string} from 'prop-types';
import cx from 'classnames';
import {noop} from '@ultradent/utilities/Functions';
import Icon from '../Icon';
import NumberBadge from '../NumberBadge';
import {useNavigate} from 'react-router-dom';

const StepLink = ( {active, visited, completed, onClick, children, ...props} ) => {
    return (visited && !active)
        ? <a {...props} onClick={onClick}>{children}</a>
        : <div {...props}>{children}</div>;
}

const ProgressStep = ( {children, step = 1, route, active, visited, completed, beforeLeave, className} ) => {
    const navigate = useNavigate();
    const handleNavigate = async ( e ) => {
        e.preventDefault();

        if ( typeof beforeLeave === 'function' ) {
            const doContinue = await beforeLeave();
            if ( !doContinue ) {
                console.log( '[ProgressStepper] - Navigation prevented by onBeforeLeave handler' );
                return;
            }
        }

        navigate( `../${route.path}` );
    }

    const stepClassName = cx(
        'progress-step t-opacity t-duration-short',
        {active, completed, visited},
        {'hover:opacity-60 cursor-pointer': !active && (completed || visited)},
        className
    );

    return (
        <StepLink
            data-testid={`ProgressStep-${step}${cx( {'-active': active} )}`}
            onClick={handleNavigate}
            active={active}
            visited={visited}
            completed={completed}
            className={stepClassName}>
            <NumberBadge className="progress-step-indicator relative">
                {completed && !active ? <Icon name="Checkmark" width="16"/> : step}
            </NumberBadge>
            <span className="progress-step-label">{children}</span>
        </StepLink>
    )
}

const ProgressStepper = ( {steps=[], progress=[], onBeforeLeave=noop, className=''} ) => {
    const activeIndex = steps.reduce( ( acc, curr, index ) => curr.active ? index : acc, 0 );

    return (
        <div data-testid="ProgressStepper" className={cx( 'progress-steps', className )}>
            {steps.map( ( step, index ) =>
                <ProgressStep
                    key={index}
                    beforeLeave={onBeforeLeave}
                    visited={progress?.[index]?.visited}
                    completed={progress?.[index]?.completed}
                    active={index === activeIndex}
                    route={steps[index]}
                    step={index + 1}>{step.label}</ProgressStep>
            )}
        </div>
    )
}

ProgressStepper.propTypes = {
    steps: arrayOf( shape( {
        active: bool,
        label: string,
        path: string
    } ) ),
    progress: arrayOf( shape( {
        visited: bool,
        completed: bool
    } ) ),
    onBeforeLeave: func
};

export default ProgressStepper;
